import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { userStore, generalStore, downloadStore, bookStore, libraryStore, wishlistStore, notificationStore } from '../../store';
import { PushNotifications } from '@capacitor/push-notifications';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

// Splash screen — matches the v2 design Mike approved 2026-05-30.
//
// Stack, top to bottom:
//   1. Black tablet-style logo tile (kleverbook_logo_new.png)
//   2. "KleverBook" wordmark in indigo (#5B47E0)
//   3. "Read. Discover. Inspire." tagline in muted indigo
//   4. Floating-icons splash artwork (kleverbook_splash_artwork.png) — open
//      book with colorful app-icon tiles floating above
//   5. Progress-dot row at the bottom (3 dots, middle filled while loading)
//
// Background is white (#FFFFFF) — the previous dark-photo version is gone
// per the new system. The illustration carries all the color.

const SplashScreen: React.FC = observer(() => {
  const navigate = useNavigate();
  // dotIndex cycles 0→1→2→0 so the row reads as "loading" not "stuck".
  const [dotIndex, setDotIndex] = useState(1);

  useEffect(() => {
    const tick = setInterval(() => {
      setDotIndex((i) => (i + 1) % 3);
    }, 500);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    const init = async () => {
      // Wait briefly so the loading animation has a moment to play; the data
      // fetch below races with this, and whichever is slower decides timing.
      const minDisplay = new Promise((resolve) => setTimeout(resolve, 1800));

      await setupPushNotifications();

      App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive && userStore.isLoggedIn) {
          try {
            const status = await import('@capacitor/network').then((m) => m.Network.getStatus());
            generalStore.setIsInternet(status.connected);
            if (status.connected) {
              userStore.attemptToGetUser();
            }
          } catch (e) {
            // silent
          }
        }
      });

      const dataFetch = (async () => {
        try {
          await Promise.all([
            userStore.getAdvertisedBooks(),
            userStore.getCategories(),
            bookStore.getSliderImages(),
            bookStore.getFeaturedBooks(),
          ]);

          if (userStore.isLoggedIn) {
            await Promise.all([
              userStore.attemptToGetUser(),
              downloadStore.getDownloadsByUser(),
              downloadStore.getDefaultAds(),
              libraryStore.getOwnedLibrary(),
              wishlistStore.load(),
            ]);
            // Notification polling — only kicks in for signed-in users.
            // Fires immediately + every 30 s. Idempotent on re-call.
            notificationStore.startPolling();
          }
        } catch (e) {
          console.log('Init error:', e);
        }
      })();

      await Promise.all([minDisplay, dataFetch]);

      generalStore.setLoading(false);
      navigate('/app/home', { replace: true });
    };

    init();
  }, [navigate]);

  const setupPushNotifications = async () => {
    // Push notifications require a Firebase project (google-services.json) on
    // Android. Without it, the native register() throws on a background thread
    // — which a JS try/catch cannot intercept — and crashes the whole app.
    // Keep this gated off until Firebase is configured. Flip the env flag to
    // 'true' once google-services.json is in android/app/.
    if (process.env.REACT_APP_PUSH_ENABLED !== 'true') {
      return;
    }
    // PWA / browser path: the Capacitor PushNotifications plugin throws
    // immediately when called without a native bridge. iOS Safari supports
    // web push only after the user installs as a PWA (iOS 16.4+) and even
    // then via a different API (the standard Push API + service worker),
    // which is a separate workstream. Quietly no-op here.
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    try {
      let permResult = await PushNotifications.checkPermissions();

      if (permResult.receive === 'prompt') {
        permResult = await PushNotifications.requestPermissions();
      }

      if (permResult.receive !== 'granted') {
        console.log('Push notification permission not granted');
        return;
      }

      await PushNotifications.register();

      PushNotifications.addListener('registration', (token) => {
        console.log('Push registration token:', token.value);
        userStore.setNotificationToken(token.value);
      });

      PushNotifications.addListener('registrationError', (error) => {
        console.log('Push registration error:', error);
      });

      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('Push notification received:', notification);
      });

      PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        console.log('Push notification action:', action);
      });
    } catch (e) {
      console.log('Push notifications not available:', e);
    }
  };

  return (
    <div
      className="relative h-full w-full overflow-hidden"
      style={{ background: '#FFFFFF' }}
    >
      <style>{splashStyles}</style>

      <div className="relative h-full w-full flex flex-col items-center px-6 pt-16 pb-10">
        {/* === Top: logo tile === */}
        <img
          src={`${process.env.PUBLIC_URL || ''}/kleverbook_logo_new.png`}
          alt="KleverBook"
          className="w-[120px] h-[120px] rounded-[28px] shadow-[0_20px_50px_-10px_rgba(91,71,224,0.25)] splash-logo"
        />

        {/* === Wordmark === */}
        <h1
          className="mt-7 text-[44px] font-semibold tracking-tight"
          style={{ color: '#5B47E0', lineHeight: 1.05 }}
        >
          KleverBook
        </h1>

        {/* === Tagline === */}
        <p
          className="mt-3 text-[16px] font-medium"
          style={{ color: '#5B47E0', opacity: 0.85, letterSpacing: '0.01em' }}
        >
          Read. Discover. Inspire.
        </p>

        {/* === Splash artwork (open book with floating icons) === */}
        <div className="flex-1 w-full flex items-center justify-center mt-10">
          <img
            src={`${process.env.PUBLIC_URL || ''}/kleverbook_splash_artwork.png`}
            alt=""
            aria-hidden="true"
            className="w-full max-w-[300px] h-auto splash-artwork"
          />
        </div>

        {/* === Progress dots === */}
        <div className="flex items-center gap-2 mt-4" aria-label="Loading">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="block rounded-full transition-all duration-300"
              style={{
                width: i === dotIndex ? 24 : 8,
                height: 8,
                background: i === dotIndex ? '#5B47E0' : '#E2E0F5',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
});

const splashStyles = `
  @keyframes splash-logo-rise {
    0%   { opacity: 0; transform: translateY(12px) scale(0.96); }
    100% { opacity: 1; transform: translateY(0)     scale(1);    }
  }
  @keyframes splash-artwork-float {
    0%, 100% { transform: translateY(0);    }
    50%      { transform: translateY(-8px); }
  }

  .splash-logo {
    animation: splash-logo-rise 0.6s ease-out both;
  }
  .splash-artwork {
    animation:
      splash-logo-rise 0.8s 0.2s ease-out both,
      splash-artwork-float 4s 1s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .splash-logo, .splash-artwork {
      animation: none;
    }
  }
`;

export default SplashScreen;
